import React, { useEffect, useRef, useState } from "react";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { PhotoCropDialog } from "./PhotoCropDialog";
import { StaffAvatar, refreshAvatars } from "./StaffAvatar";
import { api, errMsg } from "../lib/api";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp";

/**
 * Phase 86 — pick → crop → save a staff photo.
 *
 * Modes:
 *  - `userId="me"`  : the signed-in staff member changes their own photo (PUT /users/me/photo)
 *  - `userId=<id>`  : Municipal Admin changes any staff photo (PUT /users/{id}/photo)
 *  - `deferred`     : no user yet (Add User form) — keeps the cropped image in `value` and
 *                     reports it via `onValueChange(dataUrl|null)`; the parent uploads after create.
 */
export function AvatarEditor({ code, userId, name, photoUrl, onChanged, deferred = false, value, onValueChange,
  size = "xl", compact = false, testId = "avatar-editor", path: pathOverride = null }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // latest saved URL (so the button flips to "Change photo" even if the parent holds a stale record)
  const [saved, setSaved] = useState(photoUrl);
  useEffect(() => { setSaved(photoUrl); }, [photoUrl]);
  const current = deferred ? value : saved;
  // `path` lets non-tenant accounts (platform Super Admin) reuse the same pick → crop → save flow
  const path = pathOverride || `/t/${code}/users/${userId === "me" ? "me" : userId}/photo`;

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!ACCEPT.split(",").includes(f.type)) return toast.error("Please choose a JPG, PNG or WEBP image");
    if (f.size > MAX_BYTES) return toast.error("Photo is larger than 5MB — please choose a smaller image");
    setFile(f);
    setCropOpen(true);
  };

  const save = async (dataUrl) => {
    if (deferred) { onValueChange?.(dataUrl); return; }
    setBusy(true);
    try {
      const r = await api.put(path, { data: dataUrl });
      toast.success("Photo updated");
      setSaved(r.data.photo_url);
      if (code) await refreshAvatars(code);
      onChanged?.(r.data.photo_url);
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (deferred) { onValueChange?.(null); return; }
    setBusy(true);
    try {
      await api.delete(path);
      toast.success("Photo removed");
      setSaved(null);
      if (code) await refreshAvatars(code);
      onChanged?.(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={compact ? "flex items-center gap-3" : "flex flex-col items-center gap-3"} data-testid={testId}>
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
        className="relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group"
        title={current ? "Change photo" : "Upload photo"} data-testid={`${testId}-avatar-button`}>
        <StaffAvatar code={code} userId={userId === "me" ? undefined : userId} name={name} photoUrl={current ?? null} size={size} className="ring-2 ring-border" />
        <span className="absolute inset-0 rounded-full bg-black/45 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">
          <Camera className="h-5 w-5" />
        </span>
      </button>
      <div className={compact ? "flex flex-col gap-1.5" : "flex items-center gap-2"}>
        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testId}-upload-button`}>
          <Camera className="h-3.5 w-3.5 mr-1.5" /> {current ? "Change photo" : "Upload photo"}
        </Button>
        {current && (
          <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={remove} disabled={busy} data-testid={`${testId}-remove-button`}>
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
          </Button>
        )}
      </div>
      {!compact && <p className="text-[11px] text-muted-foreground">JPG, PNG or WEBP up to 5MB. You can crop and zoom before saving.</p>}
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={pick} data-testid={`${testId}-file-input`} />
      <PhotoCropDialog open={cropOpen} onOpenChange={setCropOpen} file={file} onCropped={save} />
    </div>
  );
}

/** Self-service dialog used by the portal headers: "My profile photo". */
export function ProfilePhotoDialog({ code, open, onOpenChange, user, onChanged }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="profile-photo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">My profile photo</DialogTitle>
          <DialogDescription>Your photo appears next to your name across the portal so colleagues can identify you at a glance.</DialogDescription>
        </DialogHeader>
        <AvatarEditor code={code} userId="me" name={user?.name} photoUrl={user?.photo_url || null}
          onChanged={onChanged} testId="profile-photo-editor" />
        <p className="text-sm text-center font-medium">{user?.name}</p>
      </DialogContent>
    </Dialog>
  );
}
